import { Injectable } from '@angular/core';
import { homeGroup, Menu, webviewHomeGroup } from '@app/models/menu.model';
import { RecordTagSet, RecordTitle, RecordTitleSet } from '@app/models/record.model';
import { ButtonTitle, ScheduleTitle } from '@app/models/schedule.model';
import { ApiService } from '@app/services/api.service';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { mergeMap, tap } from 'rxjs';
import { RouteCondition } from '@app/shared/enum/route';
import { RemoveDataSet } from '../data/data.classes';
import { CheckCareConne, ClearReturnPath, ClearSetting, InitAliasId, SelectTitle, SelectTitles, SetAliasId, SetButtonTitleList, SetCorpPrefix, SetMenuList, SetReturnPath, SetRouteCondition, SetScheduleTitleList, SetTitleSetList, SetTitleTagList, SetWebView, SetWebviewMenuList } from './setting.classes';

export interface Alias {
  id: string;
  name: string;
  checked?: boolean;
}

export interface SelectedTitles {
  objcd?: string;
  items: RecordTitle[];
}

export interface SelectedTitle {
  daicd: string;
  title: ScheduleTitle;
}

export interface ReturnPath {
  paths: string[];
  navigateFinally?: Function;
}

export interface SettingStateModel {
  aliasId: string;
  titleSetList: RecordTitleSet[];
  selectedTitles: SelectedTitles | null;
  scheduleTitleList: ScheduleTitle[];
  selectedTitle: SelectedTitle | null;
  menuList: Menu[];
  buttonTitleList: ButtonTitle[];
  isWebview: boolean;
  corpPrefix: string;
  isCareConneOffice: boolean,
  titleTagList: RecordTagSet[];
  routeCondition: RouteCondition;
  careconnetPass: number;
}

@State<SettingStateModel>({
  name: 'setting',
  defaults: {
    aliasId: '',
    titleSetList: [],
    selectedTitles: null,
    scheduleTitleList: [],
    selectedTitle: null,
    menuList: [],
    buttonTitleList: [],
    isWebview: false,
    corpPrefix: '',
    isCareConneOffice: false,
    titleTagList: [],
    routeCondition: RouteCondition.UNSET,
    careconnetPass: -1,
  },
})

@State<ReturnPath>({
  name: 'path',
  defaults: {
    paths: []
  }
})

@Injectable()
export class SettingState {

  private webviewAliasInitCheckKey = 'webview-setting';
  private scheduleInsertPath = 'schedule-insert-origin';

  constructor(private apiService: ApiService, public store: Store) { }

  @Action(ClearSetting)
  clearSetting(ctx: StateContext<SettingStateModel>) {
    ctx.patchState({
      titleSetList: [],
      selectedTitles: null,
      scheduleTitleList: [],
      selectedTitle: null,
      menuList: [],
      buttonTitleList: [],
    });
  }

  @Action(SetReturnPath)
  setPath(
    ctx: StateContext<ReturnPath>,
    { pathParts, navigateFinally }: SetReturnPath
  ) {
    sessionStorage.setItem(this.scheduleInsertPath, pathParts.join('/'));
    ctx.patchState({ paths: pathParts, navigateFinally: navigateFinally });
  }

  @Action(ClearReturnPath)
  clearPath(
    ctx: StateContext<ReturnPath>
  ) {
    sessionStorage.removeItem(this.scheduleInsertPath);
    ctx.patchState({ paths: [], navigateFinally: undefined });
  }

  @Action(SetTitleSetList)
  setTitleSetList(
    ctx: StateContext<SettingStateModel>,
    { aliasId }: SetTitleSetList
  ) {
    return this.apiService
      .getRecordTitleSetList(aliasId || 'list')
      .pipe(tap((titleSetList) => ctx.patchState({ titleSetList })));
  }

  @Action(SelectTitles)
  selectTitles(
    ctx: StateContext<SettingStateModel>,
    { selectedTitles }: SelectTitles
  ) {
    ctx.patchState({ selectedTitles });
  }

  @Action(SetWebView)
  selectWebView(
    ctx: StateContext<SettingStateModel>,
    { isWebview }: SetWebView
  ) {
    ctx.patchState({ isWebview });
  }


  @Action(SetScheduleTitleList)
  setScheduleTitleList(ctx: StateContext<SettingStateModel>) {
    return this.apiService
      .getScheduleTitleList()
      .pipe(tap((scheduleTitleList) => ctx.patchState({ scheduleTitleList })));
  }

  @Action(SelectTitle)
  selectTitle(
    ctx: StateContext<SettingStateModel>,
    { selectedTitle }: SelectTitle
  ) {
    ctx.patchState({ selectedTitle });
  }

  @Action(SetAliasId)
  setAliasId(ctx: StateContext<SettingStateModel>, { aliasId }: SetAliasId) {
    ctx.patchState({ aliasId });
    return ctx.dispatch([
      new SetTitleTagList(aliasId),
      new SetTitleSetList(aliasId)]
    );
  }

  @Action(InitAliasId)
  initAliasId(ctx: StateContext<SettingStateModel>) {
    this.checkWebviewSeting(ctx);
    const { aliasId } = ctx.getState();
    return this.apiService.getAliasList().pipe(
      mergeMap((list: Alias[]) => {
        const hasAlias = list.some((item) => item.id == aliasId);
        if (!hasAlias) {
          return ctx.dispatch([
            new SetAliasId(''),
            new SetTitleTagList(''),
          ]);
        }
        return ctx.dispatch([
          new SetAliasId(aliasId),
          new SetTitleTagList(aliasId),
        ]);
      })
    );
  }

  @Action(SetCorpPrefix)
  setCorpPrefix(ctx: StateContext<SettingStateModel>) {
    const _corpPrefix = localStorage.getItem('corpPrefix') || '';
    const { corpPrefix } = ctx.getState();
    if (_corpPrefix != corpPrefix) {
      this.clearSetting(ctx);
      this.store.dispatch(new RemoveDataSet);
      ctx.patchState({
        corpPrefix: _corpPrefix,
      })
      return true;
    }
    return true;
  }

  private checkWebviewSeting(ctx: StateContext<SettingStateModel>) {
    const { isWebview, aliasId } = ctx.getState();
    if (!isWebview) return;
    let webviewAlias = localStorage.getItem(this.webviewAliasInitCheckKey) || '';
    if (webviewAlias === '') {
      ctx.patchState({ aliasId: aliasId });
      return;
    }
    ctx.patchState({ aliasId: JSON.parse(webviewAlias)?.aliasId || aliasId });
  }

  @Action(SetButtonTitleList)
  setButtonTitleList(
    ctx: StateContext<SettingStateModel>,
    { isWebview, corpPrefix }: SetButtonTitleList
  ) {
    return this.apiService.getButtonTitleList(corpPrefix).pipe(
      tap((list) => {
        const buttonTitleList = list.map((item) => {
          const { ckbnid, compid, ctype, funcid, action } = item;
          const id = compid || funcid;
          const base = getBasePath({ id, ctype, action }, ckbnid, isWebview);
          const path = getPath(base, item);
          return { ...item, path };
        });
        return ctx.patchState({ buttonTitleList });
      })
    );
  }

  @Action(SetMenuList)
  setMenuList(ctx: StateContext<SettingStateModel>) {
    return this.apiService.getMenuList().pipe(
      tap((res) => {
        const list = setMenuPath(res);
        const menuList = [homeGroup, ...list];
        ctx.patchState({ menuList });
      })
    );
  }

  @Action(SetWebviewMenuList)
  setWebviewMenuList(ctx: StateContext<SettingStateModel>, { corpPrefix }: SetWebviewMenuList) {
    return this.apiService.getMenuList(corpPrefix).pipe(
      tap((res) => {
        const list = setMenuPath(res, true);
        const menuList = [webviewHomeGroup, ...list];
        ctx.patchState({ menuList });
      })
    );
  }

  @Action(SetTitleTagList)
  setTitleTagList(
    ctx: StateContext<SettingStateModel>,
    { aliasId }: SetTitleTagList
  ) {
    return this.apiService
      .getRecordTagList(aliasId || 'list')
      .pipe(tap((titleTagList) => ctx.patchState({ titleTagList })));
  }

  @Action(SetRouteCondition)
  setRouteCondition(ctx: StateContext<SettingStateModel>, { condition }: SetRouteCondition) {
    ctx.patchState({
      routeCondition: condition
    })
  }

  @Action(CheckCareConne)
  checkCareConneFeature(ctx: StateContext<SettingStateModel>) {
    // Check if unit is supported for careconne
    this.apiService.isCareConneUnit().subscribe(res => {
      ctx.patchState({
        isCareConneOffice: !!res?.status || false,
      });
    });
    // Check if user is supported for careconne
    this.apiService.getCareConnePass().subscribe(res => {
      ctx.patchState({
        careconnetPass: res?.pass || -1
      });
    });
  }
}

function setMenuPath(list: Menu[], isWebview?: boolean): Menu[] {
  return list.map(({ menus, ...menuGroup }) => ({
    ...menuGroup,
    menus: menus.map((menu) => {
      const basePath = getBasePath(menu, menuGroup.menuGroupId, isWebview);
      const chkTtls = menu.chkTtls?.map((title) => ({
        ...title,
        path: getPath(basePath, title),
      }));

      if (chkTtls && chkTtls.length > 0) {
        if (chkTtls.length === 1) {
          const title = chkTtls[0];
          return { ...menu, path: title.path, chkTtls: [] };
        }
      }

      return { ...menu, path: basePath, chkTtls };
    }),
  }));
}

interface MenuType {
  id: string;
  ctype?: string | null;
  action?: string | null;
}

function getBasePath(
  { id, ctype, action }: MenuType,
  menuGroupId: string,
  isWebview?: boolean
): string {
  const list = [
    { type: 'DAIINFOSIM', path: '/basic/info' },
    { type: 'ASSESSMENT', path: '/basic/facesheet' },
    { type: 'NOTELIST', path: '/record/list' },
    { type: 'SCHEDLIST', path: '/record/list' },
    { type: 'LASTNOTELIST', path: '/record/list' },
  ];
  const item = list.find((item) => item.type === (ctype || action));
  if (item) {
    const webview = isWebview ? '/webview' : '';
    const type = !!ctype ? 'ctype' : 'action';
    return `${webview}${item.path}/${menuGroupId}/${id}/${type}`;
  }
  return '/404';
}

interface TitleCode {
  objcd: string;
  ttlkbn: string;
  ttlcd: string;
}

function getPath(basePath: string, title: TitleCode): string {
  const { objcd, ttlkbn, ttlcd } = title;
  return !!objcd && !!ttlkbn && !!ttlcd
    ? `${basePath}/${objcd}/${ttlkbn}/${ttlcd}`
    : basePath;
}
